import { useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Link } from "react-router-dom";
import {
  BookOpen, Settings2, KeyRound, Server, Database, Bot, CreditCard, Plug, Shield, FileCode, ChevronRight, Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const SECTIONS = [
  { id: "overview", label: "Platform overview", icon: BookOpen },
  { id: "stack", label: "Architecture & files", icon: FileCode },
  { id: "env", label: "Environment variables", icon: KeyRound },
  { id: "auth", label: "Auth flow", icon: Shield },
  { id: "agents", label: "AI agents flow", icon: Bot },
  { id: "workflows", label: "Multi-agent workflows", icon: Bot },
  { id: "campaigns", label: "Campaigns flow", icon: Settings2 },
  { id: "simulation", label: "Simulation mode", icon: Settings2 },
  { id: "team", label: "Team & RBAC", icon: Shield },
  { id: "signal_score", label: "Signal Score widget", icon: Plug },
  { id: "inbox", label: "Inbox & AI-suggest flow", icon: Plug },
  { id: "content", label: "Content Studio flow", icon: BookOpen },
  { id: "analytics", label: "Analytics flow", icon: Server },
  { id: "billing", label: "Billing & Stripe flow", icon: CreditCard },
  { id: "integrations", label: "Integrations dashboard", icon: Plug },
  { id: "db", label: "MongoDB schema", icon: Database },
  { id: "edit", label: "How to edit anything", icon: FileCode },
  { id: "extend", label: "How to add a new agent / route", icon: Bot },
  { id: "swap_llm", label: "How to swap LLM provider", icon: Bot },
  { id: "phase2", label: "Phase-2 expansion", icon: Server },
  { id: "creds", label: "Demo credentials", icon: KeyRound },
];

export default function HelpPage() {
  const [q, setQ] = useState("");
  const filteredSections = SECTIONS.filter(s => !q || s.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardLayout eyebrow="Help & Setup" title="LogiGrowth AI — operator handbook">
      <div className="grid lg:grid-cols-12 gap-8">
        {/* TOC */}
        <aside className="lg:col-span-3 lg:sticky lg:top-24 self-start">
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input data-testid="help-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search docs..." className="pl-8 h-9" />
          </div>
          <nav className="border border-border rounded-sm bg-card/20 overflow-hidden">
            {filteredSections.map((s, i) => {
              const Icon = s.icon;
              return (
                <a key={s.id} href={`#${s.id}`} data-testid={`help-toc-${s.id}`}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors ${i > 0 ? "border-t border-border" : ""}`}>
                  <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>{s.label}</span>
                </a>
              );
            })}
          </nav>
        </aside>

        {/* Body */}
        <article className="lg:col-span-9 space-y-12 prose-headings:font-display prose-headings:tracking-tight">
          <Section id="overview" title="Platform overview">
            <p>
              LogiGrowth AI is an <strong>AI Revenue Operating System</strong> for B2B logistics — not a LinkedIn bot.
              The MVP ships <strong>4 live AI agents</strong> (Prospect, Research, Personalization, Safety), a campaign
              builder, prospect intelligence with logistics signals, a unified inbox, a content studio, analytics,
              4-tier Stripe billing, and a multi-tenant workspace model.
            </p>
            <p>
              The architecture is deliberately modular so Phase-2 systems (LinkedIn Playwright workers, Twilio, real
              CRM sync, n8n, vector memory) plug in without rewrites. See the <Link to="/app/integrations" className="underline">Integrations dashboard</Link> for the current connector inventory.
            </p>
          </Section>

          <Section id="stack" title="Architecture &amp; file map">
            <Sub>Backend (<Code>/app/backend</Code>)</Sub>
            <Tree lines={[
              "server.py              # FastAPI entrypoint, router includes, Stripe webhook",
              "auth.py                # JWT (HS256), bcrypt, get_current_workspace dep",
              "db.py                  # Motor MongoDB client",
              "models.py              # Pydantic schemas (signup, campaign, prospect, etc.)",
              "seed.py                # Auto-seed demo data on signup",
              "services/llm.py        # Provider-agnostic LLM wrapper (default: openai/gpt-5.2)",
              "agents/",
              "  base.py              # BaseAgent with record() helper",
              "  registry.py          # AGENTS map + PLACEHOLDER_AGENTS (phase 2)",
              "  prospect.py          # Prospect Agent",
              "  research.py          # Research Agent",
              "  personalization.py   # Personalization Agent",
              "  safety.py            # Safety Agent (local, no LLM)",
              "routes/",
              "  auth_routes.py",
              "  prospect_routes.py",
              "  campaign_routes.py",
              "  agent_routes.py",
              "  inbox_routes.py",
              "  content_routes.py",
              "  analytics_routes.py",
              "  billing_routes.py",
              "  integrations_routes.py",
            ]} />
            <Sub>Frontend (<Code>/app/frontend/src</Code>)</Sub>
            <Tree lines={[
              "App.js                 # Router + AuthProvider",
              "index.css              # Tailwind + Swiss theme tokens",
              "lib/api.js             # Axios with JWT interceptor",
              "lib/auth.jsx           # AuthProvider context",
              "lib/utils.js           # cn(), fmtNumber, relativeTime",
              "components/layout/DashboardLayout.jsx  # sidebar + topbar shell",
              "components/ThemeToggle.jsx",
              "components/ui/         # shadcn primitives",
              "pages/                 # one file per route",
            ]} />
          </Section>

          <Section id="env" title="Environment variables (and how to add a key)">
            <p>All secrets live in <Code>/app/backend/.env</Code>. After editing, restart the backend with <Code>sudo supervisorctl restart backend</Code>.</p>
            <Table rows={[
              ["MONGO_URL", "Mongo connection string", "required"],
              ["DB_NAME", "Mongo database name", "required"],
              ["CORS_ORIGINS", "Comma-list or * for dev", "required"],
              ["EMERGENT_LLM_KEY", "Universal key for OpenAI / Anthropic / Gemini via emergentintegrations", "critical"],
              ["STRIPE_API_KEY", "Stripe test key (sk_test_emergent provided)", "critical"],
              ["JWT_SECRET", "HMAC secret for tokens — change in production", "critical"],
              ["JWT_ALGORITHM", "Token algorithm (HS256)", "required"],
              ["JWT_EXPIRE_MINUTES", "Token TTL (default 10080 = 7 days)", "required"],
            ]} headers={["Key", "Purpose", "Severity"]} />
            <p className="text-sm">
              The <Link to="/app/integrations" className="underline">Integrations dashboard</Link> auto-detects which keys are present and
              flags missing critical ones with red alerts.
            </p>
          </Section>

          <Section id="auth" title="Auth flow (JWT, multi-tenant)">
            <Steps items={[
              "POST /api/auth/signup → creates user + workspace + owner membership; auto-seeds demo data; returns JWT.",
              "POST /api/auth/login → bcrypt-verifies password; returns JWT for the user's primary workspace.",
              "Frontend stores token in localStorage(lg_token) + user/workspace cached.",
              "Axios interceptor (lib/api.js) attaches `Authorization: Bearer <token>` to every request.",
              "Protected backend routes depend on `get_current_workspace` which validates JWT + membership in O(1).",
              "401 on any request triggers logout + redirect to /login.",
            ]} />
            <p className="text-sm">Edit auth: <Code>/app/backend/auth.py</Code>, <Code>routes/auth_routes.py</Code>, <Code>lib/auth.jsx</Code>.</p>
          </Section>

          <Section id="agents" title="AI agents flow">
            <p>Every agent inherits <Code>BaseAgent</Code> and records every run to <Code>agent_runs</Code>.</p>
            <Steps items={[
              "Frontend (AgentsPage / ProspectDetailPage / Inbox AI-suggest / Content Studio) → POST /api/agents/run with `agent_type` + `input` JSON.",
              "Route resolves via agents/registry.get_agent(type, workspace_id).",
              "Agent.run(input) calls services/llm.generate() with system + prompt; Safety Agent uses local heuristics only.",
              "Result is returned to client AND persisted via BaseAgent.record() to MongoDB.",
              "Agent activity stream in Overview / AgentsPage reads from /api/agents/runs.",
            ]} />
            <p className="text-sm">Edit prompts: <Code>/app/backend/agents/&lt;agent&gt;.py</Code>. Add a new agent: see <a href="#extend" className="underline">How to add a new agent</a>.</p>
          </Section>

          <Section id="campaigns" title="Campaigns flow">
            <Steps items={[
              "User opens /app/campaigns/new → CampaignBuilderPage (4-step wizard).",
              "Step 2 'Sequence' has an AI-draft button per step → calls /api/agents/run (personalization).",
              "Step 4 'Review' has a Simulate button → /api/simulations/run predicts outcomes + cost before launching.",
              "On Launch → POST /api/campaigns (creates draft) then POST /api/campaigns/{id}/launch (status=active).",
              "Phase 2: Active campaigns will dispatch jobs to the LinkedIn worker queue.",
            ]} />
          </Section>

          <Section id="workflows" title="Multi-agent workflows (LangGraph-pattern orchestrator)">
            <p>The orchestrator chains agents into reusable pipelines. Templates live in <Code>services/orchestrator.py</Code> (<Code>WORKFLOWS</Code> dict).</p>
            <Steps items={[
              "GET /api/workflows lists available workflow templates.",
              "POST /api/workflows/run { workflow_id, inputs } executes the pipeline; each step's output becomes part of shared `state`.",
              "Built-in workflows: prospect_discovery (research → personalize → safety), content_pipeline (content → engagement), engagement_optimizer, analytics_report.",
              "All runs persisted to `workflow_runs` collection — observable from the Workflows page.",
            ]} />
            <p className="text-sm">Add a new workflow by appending an entry to <Code>WORKFLOWS</Code>; the frontend picks it up automatically.</p>
          </Section>

          <Section id="simulation" title="Simulation mode">
            <p>Predict campaign outcomes (sent / accepted / replied / meetings / pipeline / AI cost) BEFORE you launch.</p>
            <Steps items={[
              "POST /api/simulations/run { campaign, days } — uses workspace 14d baselines + safety config + sequence depth.",
              "Generates 3 sample AI-personalized messages for the top-scored prospects.",
              "Surfaces risk notes (excessive daily limit, single-touch sequences, fatigue risk).",
            ]} />
            <p className="text-sm">UI: campaign builder review step → "Simulate first" button before "Launch campaign".</p>
          </Section>

          <Section id="team" title="Team & RBAC">
            <p>Workspace-scoped role-based access control with three roles: <Code>owner</Code>, <Code>admin</Code>, <Code>member</Code>.</p>
            <Steps items={[
              "GET /api/team/members → current roster + pending invites.",
              "POST /api/team/invites { email, role } → returns a signed JWT invite token; share /invite/<token> link.",
              "POST /api/team/invites/accept { token, name, password } → creates user (if new) + membership, returns auth token.",
              "PATCH /api/team/members/{id} role updates; cannot demote the last owner.",
              "DELETE /api/team/members/{id} removes a member (not owner).",
              "Plan seat limits enforced via services/plan_limits.py (Starter=1, Team=10, Agency=50, Enterprise=unlimited).",
            ]} />
          </Section>

          <Section id="signal_score" title="Logistics Signal Score widget">
            <p>Public, unauthenticated scoring service — the inbound growth lever.</p>
            <Steps items={[
              "GET /api/public/score?company=X&narrative=true → returns 0-100 score, tier (Cold/Warming/Hot/Red-Hot), 8 weighted components, optional AI narrative.",
              "Public landing page: /score (search) and /score/:company (full report).",
              "Embeddable: /score/:company?embed=1 returns a compact iframe-safe widget.",
              "Scores cached in `signal_scores` collection; recompute by passing refresh=true (future).",
            ]} />
            <p className="text-sm">Scoring engine: <Code>services/signal_scoring.py</Code>. v1 uses deterministic SHA256-based heuristic over 8 logistics signal categories. v2 will mix in real public data sources.</p>
          </Section>

          <Section id="inbox" title="Inbox &amp; AI-suggest flow">
            <Steps items={[
              "GET /api/inbox/conversations lists threads; click → GET /api/inbox/conversations/{id} loads messages.",
              "POST /api/inbox/reply persists an outbound message (channel inherited from conversation).",
              "POST /api/inbox/ai-suggest gathers prospect + last 4 messages → calls Personalization Agent → returns suggested reply.",
              "Phase 2: LinkedIn / WhatsApp / Email inbound webhooks will push real messages here.",
            ]} />
          </Section>

          <Section id="content" title="Content Studio flow">
            <Steps items={[
              "User enters topic + tone → POST /api/content/generate.",
              "Backend calls services/llm.generate() with a logistics-tuned system prompt.",
              "Draft saved to `content_drafts`; listed via GET /api/content/drafts; delete via DELETE.",
              "Phase 2: scheduling, multi-image posts, employee advocacy redistribution.",
            ]} />
          </Section>

          <Section id="analytics" title="Analytics flow">
            <p>GET <Code>/api/analytics/overview</Code> aggregates:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Counts: prospects, campaigns (total / active), conversations</li>
              <li>14d sums: sent / accepted / replied / meetings (from <Code>analytics_events</Code>)</li>
              <li>Derived rates: acceptance %, reply %, meeting % + pipeline $ heuristic</li>
              <li>Status distribution (for the pie chart)</li>
            </ul>
          </Section>

          <Section id="billing" title="Billing &amp; Stripe flow">
            <Steps items={[
              "GET /api/billing/plans → 4 server-defined plans (never trust client amounts).",
              "POST /api/billing/checkout {plan_id, origin_url} → backend builds success/cancel URLs from origin, calls emergentintegrations StripeCheckout.create_checkout_session, persists a `payment_transactions` row with status=pending.",
              "Frontend redirects to the returned Stripe URL.",
              "On return Stripe appends ?session_id=… → BillingPage polls GET /api/billing/checkout/status/{id}; gracefully handles 'unknown' to keep polling.",
              "When status=paid → backend upgrades workspace.plan (idempotent).",
              "POST /api/webhook/stripe is the Stripe webhook fallback path.",
            ]} />
            <p className="text-sm">Test card: <Code>4242 4242 4242 4242</Code> · any future date · any CVC.</p>
          </Section>

          <Section id="integrations" title="Integrations operations layer">
            <p>
              The <Link to="/app/integrations" className="underline">Integrations</Link> page is the operational
              control plane — every connector with verify / configure / history / usage / audit.
            </p>
            <Steps items={[
              "GET /api/integrations → list with live status (connected / degraded / invalid_credentials / quota_exceeded / rate_limited / disconnected / coming_soon).",
              "POST /api/integrations/{id}/verify → executes a real test call (OpenAI completion, Stripe session, MongoDB ping, JWT sign/verify). Results persisted to `integration_checks`.",
              "GET /api/integrations/{id}/history → last N verification runs with latency.",
              "GET /api/integrations/{id}/usage → call counts, token estimates, $ cost.",
              "PUT /api/integrations/{id}/credentials → save per-workspace credentials (Fernet-encrypted in `workspace_credentials`). Owner/admin only.",
              "Per-workspace credentials override .env at runtime via services/credentials.get_workspace_credential.",
              "GET /api/integrations/audit → full audit trail (toggle, verify, credentials_updated, credentials_removed).",
              "PATCH /api/integrations/{id} → workspace on/off toggle; plan-gated via services/plan_limits.integration_allowed.",
            ]} />
            <p className="text-sm">Add a new connector: append a dict to <Code>INTEGRATIONS</Code> in <Code>routes/integrations_routes.py</Code> and (optionally) a verifier in <Code>services/integration_verifier.py</Code>.</p>
          </Section>

          <Section id="db" title="MongoDB schema">
            <Table headers={["Collection", "Purpose", "Key indexes"]} rows={[
              ["users", "Accounts", "email (unique)"],
              ["workspaces", "Tenant orgs", "id (unique)"],
              ["memberships", "User↔workspace + role", "(user_id, workspace_id)"],
              ["prospects", "Leads + signals + score", "(workspace_id, score desc)"],
              ["campaigns", "Outbound campaigns + sequence + metrics", "(workspace_id, created_at desc)"],
              ["conversations", "Inbox threads", "(workspace_id, last_message_at desc)"],
              ["messages", "Individual messages", "(conversation_id, created_at)"],
              ["agent_runs", "AI agent execution log", "(workspace_id, created_at desc)"],
              ["content_drafts", "Content Studio outputs", "by workspace_id"],
              ["analytics_events", "Daily aggregated metrics", "by workspace_id, date"],
              ["payment_transactions", "Stripe sessions", "by session_id"],
              ["workspace_integrations", "Per-workspace connector toggles", "(workspace_id, integration_id)"],
            ]} />
            <p className="text-sm">All <Code>_id</Code> fields are excluded from API responses; resource ids are UUIDs.</p>
          </Section>

          <Section id="edit" title="How to edit anything">
            <Sub>Change a colour or font</Sub>
            <p className="text-sm">Tokens live in <Code>/app/frontend/src/index.css</Code> (CSS variables) and <Code>tailwind.config.js</Code> (signal / amber palettes, font families). Hot reload picks it up instantly.</p>
            <Sub>Edit an AI prompt</Sub>
            <p className="text-sm">Each agent's <Code>system</Code> and <Code>prompt</Code> strings sit inside its own file at <Code>/app/backend/agents/&lt;name&gt;.py</Code>. Save → backend hot-reloads.</p>
            <Sub>Change Stripe plan prices</Sub>
            <p className="text-sm">Edit the <Code>PLANS</Code> dict in <Code>/app/backend/routes/billing_routes.py</Code>. Pricing on the marketing landing is in <Code>/app/frontend/src/pages/LandingPage.jsx</Code> (<Code>PLANS</Code> array).</p>
            <Sub>Tweak demo seed data</Sub>
            <p className="text-sm"><Code>/app/backend/seed.py</Code> — companies, titles, signals, sample campaigns all live here.</p>
            <Sub>Rename or rebrand</Sub>
            <p className="text-sm">Search for <Code>LogiGrowth</Code> across <Code>/app</Code>. Update the wordmark in DashboardLayout + LandingPage. Update PRD.md + test_credentials.md to keep docs in sync.</p>
          </Section>

          <Section id="extend" title="How to add a new agent / route">
            <Sub>Add a new AI agent</Sub>
            <ol className="list-decimal pl-5 space-y-1 text-sm">
              <li>Create <Code>/app/backend/agents/my_agent.py</Code> extending <Code>BaseAgent</Code> with <Code>agent_type</Code>, <Code>display_name</Code>, <Code>description</Code>.</li>
              <li>Implement <Code>async def run(self, input)</Code>; call <Code>await services.llm.generate(system, prompt)</Code>; then <Code>await self.record(input, result)</Code>.</li>
              <li>Register in <Code>agents/registry.py</Code> — add to <Code>AGENTS</Code> dict (move out of <Code>PLACEHOLDER_AGENTS</Code> if applicable).</li>
              <li>It appears automatically in /app/agents.</li>
            </ol>
            <Sub>Add a new backend route module</Sub>
            <ol className="list-decimal pl-5 space-y-1 text-sm">
              <li>Create <Code>/app/backend/routes/my_module_routes.py</Code> exposing an <Code>APIRouter(prefix="/my_module")</Code>.</li>
              <li>Add <Code>from routes.my_module_routes import router as my_module_router</Code> and <Code>api_router.include_router(my_module_router)</Code> in <Code>server.py</Code>.</li>
              <li>Every endpoint should depend on <Code>get_current_workspace</Code> for multi-tenant safety.</li>
            </ol>
          </Section>

          <Section id="swap_llm" title="How to swap LLM provider">
            <p className="text-sm">All LLM calls flow through <Code>services/llm.py</Code>:</p>
            <Code block>{`from emergentintegrations.llm.chat import LlmChat, UserMessage

DEFAULT_PROVIDER = "openai"   # → "anthropic" or "gemini"
DEFAULT_MODEL    = "gpt-5.2"  # → "claude-sonnet-4-6" or "gemini-3.1-pro-preview"
`}</Code>
            <p className="text-sm">Change the two constants → restart backend. No agent code changes needed.</p>
          </Section>

          <Section id="phase2" title="Phase-2 expansion plan">
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li><strong>LinkedIn workers</strong>: containerised Playwright cluster behind Redis+BullMQ; campaign launch pushes jobs.</li>
              <li><strong>Twilio</strong>: SMS, WhatsApp Business, Voice/Flex — wire via integrations_routes registry.</li>
              <li><strong>CRM sync</strong>: HubSpot first (richest free tier); add Salesforce/Pipedrive after.</li>
              <li><strong>Vector memory</strong>: Pinecone or pgvector for long-term prospect / company context.</li>
              <li><strong>n8n</strong>: visual workflows triggered by webhooks for cross-tool automations.</li>
              <li><strong>Stripe subscriptions</strong>: switch one-shot Checkout to recurring; enforce plan limits via middleware.</li>
              <li><strong>RBAC</strong>: enforce role on membership in <Code>get_current_workspace</Code>; expose team-invites endpoint.</li>
            </ul>
          </Section>

          <Section id="creds" title="Demo credentials">
            <div className="border border-border rounded-sm p-4 bg-card/30">
              <div className="text-xs text-muted-foreground">Pre-seeded account</div>
              <div className="mt-2 font-mono text-sm">demo@logigrowth.ai · Demo1234!</div>
              <div className="mt-1 text-xs text-muted-foreground">workspace: DemoCo Logistics · plan: starter (trial)</div>
            </div>
            <p className="text-sm mt-3">
              Also stored in <Code>/app/memory/test_credentials.md</Code>. New signups auto-seed identical demo data.
            </p>
          </Section>

          <div className="pt-10 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
            <span>Last updated: {new Date().toLocaleDateString()}</span>
            <Link to="/app/integrations" className="hover:text-foreground inline-flex items-center gap-1">
              Open integrations dashboard <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </article>
      </div>
    </DashboardLayout>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`help-section-${id}`}>
      <h2 className="font-display font-semibold text-2xl tracking-tight mb-4">{title}</h2>
      <div className="space-y-3 text-[0.92rem] leading-relaxed text-foreground/90">{children}</div>
    </section>
  );
}
function Sub({ children }) { return <h3 className="font-display font-semibold text-base mt-4 mb-2">{children}</h3>; }
function Code({ children, block }) {
  if (block) {
    return <pre className="bg-muted/50 border border-border rounded-sm p-3 font-mono text-xs overflow-x-auto"><code>{children}</code></pre>;
  }
  return <code className="font-mono text-[0.85em] px-1 py-0.5 bg-muted/60 rounded-sm border border-border">{children}</code>;
}
function Tree({ lines }) {
  return <pre className="bg-muted/40 border border-border rounded-sm p-3 font-mono text-xs overflow-x-auto">{lines.join("\n")}</pre>;
}
function Steps({ items }) {
  return (
    <ol className="space-y-2 list-none counter-reset-step">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 text-sm">
          <span className="flex-shrink-0 w-5 h-5 rounded-sm bg-foreground text-background flex items-center justify-center font-mono text-[0.65rem]">{i + 1}</span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  );
}
function Table({ headers, rows }) {
  return (
    <div className="border border-border rounded-sm overflow-hidden">
      <div className="grid bg-muted/40 px-4 py-2 text-[0.65rem] uppercase tracking-widest font-semibold text-muted-foreground" style={{ gridTemplateColumns: `repeat(${headers.length}, minmax(0, 1fr))` }}>
        {headers.map((h) => <div key={h}>{h}</div>)}
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid border-t border-border px-4 py-2 text-sm" style={{ gridTemplateColumns: `repeat(${headers.length}, minmax(0, 1fr))` }}>
          {r.map((c, j) => <div key={j} className={j === 0 ? "font-mono text-xs" : ""}>{c}</div>)}
        </div>
      ))}
    </div>
  );
}
